import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ClipboardCheck, CheckCircle2 } from 'lucide-react';

/** Staff handover feed — unacknowledged first, acknowledge inline (Phase 1 API). */
export default function HandoverFeedPage() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const [ackBusy, setAckBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/handover/my-feed');
      const data = r.data.data || [];
      data.sort((a, b) => Number(a.acknowledged_by_me) - Number(b.acknowledged_by_me));
      setItems(data);
      setError(false);
    } catch {
      setError(true);
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async (id) => {
    setAckBusy(id);
    try {
      await api.post(`/handover/${id}/acknowledge`, {});
      toast.success('Handover acknowledged');
      await load();
      window.dispatchEvent(new Event('staff-v2-handover-ack'));
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Unable to acknowledge');
    } finally {
      setAckBusy(null);
    }
  };

  if (items === null) {
    return <div className="py-12 text-center text-sm text-muted-foreground" data-testid="handover-feed-loading">Loading handovers…</div>;
  }
  if (error) {
    return (
      <div className="py-12 text-center" data-testid="handover-feed-error">
        <p className="text-sm text-muted-foreground mb-3">Unable to load handovers.</p>
        <Button variant="outline" size="sm" onClick={load} data-testid="handover-feed-retry">Retry</Button>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground" data-testid="handover-feed-empty">
        <ClipboardCheck className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        No handovers in the last 14 days.
      </div>
    );
  }

  return (
    <div className="space-y-3 max-w-3xl" data-testid="handover-feed">
      {items.map((h) => (
        <Card key={h.id} data-testid={`handover-card-${h.id}`}>
          <CardContent className="py-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-medium">
                {h.staff_from_name || 'Outgoing staff'}
                <span className="text-muted-foreground font-normal"> · {new Date(h.created_at).toLocaleString()}</span>
              </div>
              {h.acknowledged_by_me
                ? <Badge variant="secondary" className="gap-1" data-testid={`handover-acked-${h.id}`}><CheckCircle2 className="h-3 w-3" />Acknowledged</Badge>
                : <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`handover-unread-${h.id}`}>Unread</Badge>}
            </div>
            <p className="text-sm text-foreground ">{h.handover_summary}</p>
            {h.tasks_for_next_shift && <p className="text-xs text-muted-foreground">Follow-up: {h.tasks_for_next_shift}</p>}
            {!h.acknowledged_by_me && (
              <Button size="sm" onClick={() => acknowledge(h.id)} disabled={ackBusy === h.id}
                      data-testid={`handover-ack-btn-${h.id}`}>
                {ackBusy === h.id ? 'Acknowledging…' : 'Acknowledge'}
              </Button>
            )}
            {h.acknowledgements?.length > 0 && (
              <p className="text-[11px] text-muted-foreground">
                Acknowledged by {h.acknowledgements.map((a) => a.staff_name || 'staff').join(', ')}
              </p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
